/**
 * pcg.ts — deterministic procedural content generation core (sim-side, render-free).
 *
 * Owns the seeded PRNG, value-noise field synthesis, and the canonical
 * Heightfield for the Latium battlefield: 5 km x 5 km, height range
 * clamped to [-7.5, 128] m per the Unreal L_Skirmish_Latium_5km substrate.
 *
 * Everything here is a pure function of the seed string: same seed ->
 * identical terrain, rivers, woodland mask, and farm plots. No Math.random,
 * no Date, no platform-dependent transcendentals in the hash path (integer
 * mixing only), so results are stable across runs and machines.
 *
 * The render layer (src/render/environment/) consumes this; the sim can use
 * `heightAt` anywhere a `(x: number, z: number) => number` is expected
 * (e.g. NavSystem's heightAt option).
 */

export const MAP_SIZE_M = 5000;
export const MAP_HALF_M = MAP_SIZE_M / 2;
export const MIN_HEIGHT_M = -7.5;
export const MAX_HEIGHT_M = 128;

/** FNV-1a 32-bit string hash -> unsigned seed. */
export function hashSeedString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 seeded PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Small OO wrapper over a seeded stream for placement code. */
export class Rng {
  private readonly next: () => number;
  constructor(seed: number | string) {
    this.next = mulberry32(typeof seed === "string" ? hashSeedString(seed) : seed);
  }
  /** [0, 1) */
  uniform(): number {
    return this.next();
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number): number {
    return Math.floor(this.range(a, b + 1));
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  /** Weighted pick; weights must be non-negative. Returns the index. */
  weightedIndex(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) total += w;
    let r = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r <= 0) return i;
    }
    return weights.length - 1;
  }
}

/** Integer lattice hash -> [0,1). Deterministic on every platform. */
function lattice(ix: number, iz: number, seed: number): number {
  let h = Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iz, 0x165667b1) ^ seed;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smootherstep(t: number): number {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return c * c * c * (c * (c * 6 - 15) + 10);
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Seeded 2D value noise with fractal Brownian motion. */
export class Noise2D {
  private readonly seed: number;
  constructor(seed: number | string) {
    this.seed = typeof seed === "string" ? hashSeedString(seed) : seed >>> 0;
  }
  /** Value noise in [0,1]. x/z are in arbitrary continuous units. */
  noise(x: number, z: number): number {
    const xi = Math.floor(x);
    const zi = Math.floor(z);
    const xf = x - xi;
    const zf = z - zi;
    const a = lattice(xi, zi, this.seed);
    const b = lattice(xi + 1, zi, this.seed);
    const c = lattice(xi, zi + 1, this.seed);
    const d = lattice(xi + 1, zi + 1, this.seed);
    const u = smootherstep(xf);
    const v = smootherstep(zf);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  }
  /** fbm in roughly [0,1]. */
  fbm(x: number, z: number, octaves: number, lacunarity = 2.0, gain = 0.5): number {
    let amp = 0.5;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amp * this.noise(x * freq, z * freq);
      norm += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / norm;
  }
}

export interface RiverPoint {
  x: number;
  z: number;
}

/** A river: centerline polyline (sampled ~every 25 m) + half-width in meters. */
export interface River {
  points: RiverPoint[];
  halfWidth: number;
}

export type CropKind = "wheat" | "vines" | "olives";

/** A cultivated field plot (decorative rural detail). */
export interface FieldPlot {
  cx: number;
  cz: number;
  w: number;
  d: number;
  rot: number;
  crop: CropKind;
}

export interface HeightfieldOpts {
  /** World size in meters. Default 5000 (canonical Latium). */
  size?: number;
  /**
   * Vertical exaggeration of the relief. Default 1 (canonical meters).
   * The skirmish game passes ~0.3 so hills fit the gameplay vertical
   * budget; horizontal feature layout always scales with `size`.
   */
  relief?: number;
}

/**
 * Canonical deterministic heightfield for the Latium map.
 *
 * Coordinate space: x/z in world units, centered on the map (range ±size/2).
 * y-up heights. At size = 5000, relief = 1 this is the canonical 5 km
 * Latium field (heights in meters, clamped to [-7.5, 128]); smaller sizes
 * with relief < 1 produce the same landscape character at skirmish scale.
 */
export class Heightfield {
  readonly size: number;
  readonly half: number;
  readonly seed: string;
  /** Vertical exaggeration applied to every height amplitude (default 1). */
  readonly relief: number;
  readonly rivers: River[];
  readonly fields: FieldPlot[];
  /** Natural lake spot (bed coords + radius), or null when the low point is too high. */
  readonly lake: { x: number; z: number; radius: number; bedY: number } | null;

  private readonly continent: Noise2D;
  private readonly hills: Noise2D;
  private readonly detail: Noise2D;
  private readonly ridgeNoise: Noise2D;
  private readonly moistureNoise: Noise2D;
  private readonly woodlandNoise: Noise2D;
  /**
   * Horizontal feature scale = size / 5000. All wavelengths, river widths,
   * and placement constants scale with it, so a 400-unit map reads as the
   * same Latium landscape as the canonical 5 km one (k = 1 -> bit-identical
   * output). River half-widths use sqrt(k) so rivers stay visible
   * landmarks on small maps instead of shrinking to threads.
   */
  private readonly k: number;
  /** Frequency multiplier = 1/k: keeps the feature count constant per map. */
  private readonly fk: number;
  /** Coarse river-distance grid (cell 50 m) for O(1) queries. */
  private readonly riverDistGrid: Float32Array;
  private readonly riverGridN = 100;

  constructor(seed: string, opts: HeightfieldOpts = {}) {
    this.seed = seed;
    this.size = opts.size ?? MAP_SIZE_M;
    this.half = this.size / 2;
    this.relief = opts.relief ?? 1;
    this.k = this.size / MAP_SIZE_M;
    this.fk = 1 / this.k;
    const s = hashSeedString(seed);
    this.continent = new Noise2D(s ^ 0x9e3779b9);
    this.hills = new Noise2D(s ^ 0x3c6ef372);
    this.detail = new Noise2D(s ^ 0xdaa66d2b);
    this.ridgeNoise = new Noise2D(s ^ 0x78dde6e4);
    this.moistureNoise = new Noise2D(s ^ 0x1715e8f2);
    this.woodlandNoise = new Noise2D(s ^ 0x4b1f3c55);
    this.rivers = this.generateRivers();
    this.riverDistGrid = this.buildRiverDistGrid();
    this.fields = this.generateFields();
    this.lake = this.computeLake();
  }

  // ---------------------------------------------------------------- rivers

  private generateRivers(): River[] {
    const rng = new Rng(hashSeedString(this.seed + ":rivers"));
    const k = this.k;
    const wk = Math.sqrt(k); // river widths: stay visible on small maps
    const rivers: River[] = [];
    // Main river: enters the west edge, meanders east, exits the east edge.
    const zBase = rng.range(-600, 900) * k;
    const ctrl: RiverPoint[] = [];
    const n = 9;
    for (let i = 0; i < n; i++) {
      const x = -this.half + (this.size * i) / (n - 1);
      const z = zBase + Math.sin((i / (n - 1)) * Math.PI * 2.2) * rng.range(250, 550) * k +
        rng.range(-160, 160) * k;
      ctrl.push({ x, z });
    }
    rivers.push({ points: sampleCatmullRom(ctrl, Math.max(2, 25 * k)), halfWidth: 11 * wk });
    // Tributary: descends from the northern hills to join the main river.
    const joinT = rng.range(0.35, 0.65);
    const join = ctrl[Math.floor(joinT * (n - 1))];
    const tCtrl: RiverPoint[] = [];
    const tn = 6;
    for (let i = 0; i < tn; i++) {
      const t = i / (tn - 1);
      tCtrl.push({
        x: join.x - 900 * k * (1 - t) + rng.range(-120, 120) * k,
        z: -this.half + 300 * k + t * (join.z + this.half - 300 * k) + rng.range(-140, 140) * k,
      });
    }
    rivers.push({ points: sampleCatmullRom(tCtrl, Math.max(2, 25 * k)), halfWidth: 6 * wk });
    return rivers;
  }

  private buildRiverDistGrid(): Float32Array {
    const n = this.riverGridN;
    const cell = this.size / n;
    const grid = new Float32Array(n * n).fill(1e9);
    for (const river of this.rivers) {
      const pts = river.points;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const reach = river.halfWidth * 6 + cell;
        const gx0 = Math.max(0, Math.floor((p.x - reach + this.half) / cell));
        const gx1 = Math.min(n - 1, Math.floor((p.x + reach + this.half) / cell));
        const gz0 = Math.max(0, Math.floor((p.z - reach + this.half) / cell));
        const gz1 = Math.min(n - 1, Math.floor((p.z + reach + this.half) / cell));
        for (let gz = gz0; gz <= gz1; gz++) {
          for (let gx = gx0; gx <= gx1; gx++) {
            const cx = (gx + 0.5) * cell - this.half;
            const cz = (gz + 0.5) * cell - this.half;
            const d = Math.hypot(p.x - cx, p.z - cz);
            const idx = gz * n + gx;
            if (d < grid[idx]) grid[idx] = d;
          }
        }
      }
    }
    return grid;
  }

  /** Approximate distance in meters to the nearest river centerline. */
  riverDistAt(x: number, z: number): number {
    const n = this.riverGridN;
    const cell = this.size / n;
    const gx = (x + this.half) / cell - 0.5;
    const gz = (z + this.half) / cell - 0.5;
    const x0 = Math.max(0, Math.min(n - 2, Math.floor(gx)));
    const z0 = Math.max(0, Math.min(n - 2, Math.floor(gz)));
    const fx = Math.max(0, Math.min(1, gx - x0));
    const fz = Math.max(0, Math.min(1, gz - z0));
    const a = this.riverDistGrid[z0 * n + x0];
    const b = this.riverDistGrid[z0 * n + x0 + 1];
    const c = this.riverDistGrid[(z0 + 1) * n + x0];
    const d = this.riverDistGrid[(z0 + 1) * n + x0 + 1];
    return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
  }

  // ---------------------------------------------------------------- fields

  private generateFields(): FieldPlot[] {
    const rng = new Rng(hashSeedString(this.seed + ":fields"));
    const k = this.k;
    const plots: FieldPlot[] = [];
    // Canonical farm rules (from biomes.json farm_placement):
    // max slope 12 deg, within 400 m of water, >= 180 m between farms.
    for (let attempt = 0; attempt < 60 && plots.length < 14; attempt++) {
      const cx = rng.range(-this.half + 300 * k, this.half - 300 * k);
      const cz = rng.range(-this.half + 300 * k, this.half - 300 * k);
      if (this.slopeAt(cx, cz) > Math.tan((12 * Math.PI) / 180)) continue;
      if (this.riverDistAt(cx, cz) > 400 * k) continue;
      if (this.woodlandAt(cx, cz) > 0.55) continue;
      let ok = true;
      for (const p of plots) {
        if (Math.hypot(p.cx - cx, p.cz - cz) < 180 * k) { ok = false; break; }
      }
      if (!ok) continue;
      const crop = rng.pick(["wheat", "wheat", "vines", "olives"] as const);
      const sizeRange =
        crop === "wheat" ? [40, 90] : crop === "vines" ? [25, 55] : [30, 70];
      // Footprints scale with the map but keep a minimum so olive rows and
      // wheat texture stay visible on small maps.
      const minSize = crop === "wheat" ? 14 : 10;
      const w = Math.max(minSize, rng.range(sizeRange[0], sizeRange[1]) * k);
      const d = Math.max(minSize, rng.range(sizeRange[0], sizeRange[1]) * k);
      plots.push({ cx, cz, w, d, rot: rng.range(0, Math.PI), crop });
    }
    return plots;
  }

  /** Field plot containing (x,z), or null. */
  fieldAt(x: number, z: number): FieldPlot | null {
    for (const p of this.fields) {
      const dx = x - p.cx;
      const dz = z - p.cz;
      const c = Math.cos(-p.rot);
      const s = Math.sin(-p.rot);
      const lx = dx * c - dz * s;
      const lz = dx * s + dz * c;
      if (Math.abs(lx) <= p.w / 2 && Math.abs(lz) <= p.d / 2) return p;
    }
    return null;
  }

  // ------------------------------------------------------------ sampling

  /**
   * Raw terrain height (rivers carved, clamped to the canonical range scaled
   * by relief). Horizontal feature layout scales with the map size; vertical
   * amplitudes scale with relief. relief = 1, size = 5000 reproduces the
   * canonical Latium field exactly.
   */
  heightAt(x: number, z: number): number {
    const fk = this.fk;
    const r = this.relief;
    const continent = this.continent.fbm(x * 0.0003 * fk, z * 0.0003 * fk, 4);
    const hills = this.hills.fbm(x * 0.0013 * fk + 13.7, z * 0.0013 * fk - 4.2, 4);
    const detail = this.detail.fbm(x * 0.006 * fk + 3.1, z * 0.006 * fk + 9.4, 2);
    // Northern ridgeline (Monti Sabini direction): rises toward the north edge.
    const ridgeT = clamp01((-z - 1400 * this.k) / (900 * this.k));
    const ridge = ridgeT * ridgeT * 52 * r * (0.6 + 0.4 * this.ridgeNoise.fbm(x * 0.0008 * fk, 7.7, 3));
    let h = 8 * r + (continent - 0.5) * 64 * r + (hills - 0.5) * 22 * r + (detail - 0.5) * 4 * r + ridge;
    // River carving: smooth channel with soft banks.
    const rd = this.riverDistAt(x, z);
    for (const river of this.rivers) {
      const t = clamp01(1 - rd / (river.halfWidth * 3.2));
      if (t > 0) h -= 6 * r * t * t * (3 - 2 * t);
    }
    const lo = MIN_HEIGHT_M * r;
    const hi = MAX_HEIGHT_M * r;
    if (h < lo) h = lo;
    if (h > hi) h = hi;
    return h;
  }

  /** Slope magnitude (rise/run) via central differences, epsilon 2 m. */
  slopeAt(x: number, z: number): number {
    const e = 2;
    const sx = (this.heightAt(x + e, z) - this.heightAt(x - e, z)) / (2 * e);
    const sz = (this.heightAt(x, z + e) - this.heightAt(x, z - e)) / (2 * e);
    return Math.hypot(sx, sz);
  }

  /** Soil moisture 0..1: high near rivers and in damp hollows. */
  moistureAt(x: number, z: number): number {
    const riverT = clamp01(1 - this.riverDistAt(x, z) / (220 * this.k));
    const n = this.moistureNoise.fbm(x * 0.0011 * this.fk + 5.2, z * 0.0011 * this.fk - 8.8, 3);
    return clamp01(0.25 + 0.55 * riverT * riverT + 0.45 * (n - 0.5));
  }

  /** Woodland canopy density 0..1: patchy Mediterranean forest clusters. */
  woodlandAt(x: number, z: number): number {
    const n = this.woodlandNoise.fbm(x * 0.0009 * this.fk + 31.7, z * 0.0009 * this.fk - 17.3, 3);
    const m = this.moistureAt(x, z);
    const t = (n - 0.48) / 0.24 + (m - 0.35) * 0.9;
    return clamp01(t);
  }

  /**
   * Natural lake spot, computed once: the lowest point away from rivers.
   * Shared by the water renderer and sim queries so they always agree.
   */
  private computeLake(): { x: number; z: number; radius: number; bedY: number } | null {
    const k = this.k;
    let bx = 0, bz = 0, bh = Infinity;
    for (let j = 0; j < 40; j++) {
      for (let i = 0; i < 40; i++) {
        const x = -this.half + (this.size * i) / 39;
        const z = -this.half + (this.size * j) / 39;
        if (this.riverDistAt(x, z) < 120 * k) continue;
        const h = this.heightAt(x, z);
        if (h < bh) { bh = h; bx = x; bz = z; }
      }
    }
    if (!isFinite(bh) || bh > 2 * this.relief) return null;
    return { x: bx, z: bz, radius: 70 * Math.sqrt(k), bedY: bh };
  }

  /**
   * True when (x,z) is under open water: inside a river channel (where the
   * carve exceeds the water surface offset) or inside the lake disc.
   * The relief cancels out of the river test, so it holds at any relief.
   */
  isWaterAt(x: number, z: number): boolean {
    const rd = this.riverDistAt(x, z);
    let maxHalf = 0;
    for (const river of this.rivers) maxHalf = Math.max(maxHalf, river.halfWidth);
    if (rd < maxHalf * 2.18) return true;
    const lake = this.lake;
    if (lake && Math.hypot(x - lake.x, z - lake.z) < lake.radius) return true;
    return false;
  }

  /**
   * Deterministic digest of the heightfield over an n x n sample grid.
   * Used by tests to prove seeded determinism.
   */
  hashGrid(n = 48): string {
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;
    const mix = (v: number) => {
      const q = Math.round(v * 1000);
      h1 = Math.imul(h1 ^ (q & 0xffff), 0x01000193);
      h2 = Math.imul(h2 ^ ((q >>> 16) & 0xffff), 0x85ebca6b);
      h1 ^= h2 >>> 13;
    };
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        const x = -this.half + (this.size * i) / (n - 1);
        const z = -this.half + (this.size * j) / (n - 1);
        mix(this.heightAt(x, z));
        mix(this.moistureAt(x, z));
        mix(this.woodlandAt(x, z));
      }
    }
    return ((h1 >>> 0).toString(16).padStart(8, "0") +
      (h2 >>> 0).toString(16).padStart(8, "0"));
  }
}

/** Sample a Catmull-Rom spline through control points every ~step meters. */
export function sampleCatmullRom(ctrl: RiverPoint[], step: number): RiverPoint[] {
  const out: RiverPoint[] = [];
  if (ctrl.length < 2) return ctrl.slice();
  const pts = [ctrl[0], ...ctrl, ctrl[ctrl.length - 1]];
  for (let i = 1; i < pts.length - 2; i++) {
    const p0 = pts[i - 1], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2];
    const segLen = Math.hypot(p2.x - p1.x, p2.z - p1.z);
    const steps = Math.max(2, Math.ceil(segLen / step));
    for (let s2 = 0; s2 < steps; s2++) {
      const t = s2 / steps;
      const t2 = t * t, t3 = t2 * t;
      out.push({
        x: 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        z: 0.5 * (2 * p1.z + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
      });
    }
  }
  out.push({ ...ctrl[ctrl.length - 1] });
  return out;
}

/** Convenience: build the canonical Latium heightfield for a seed. */
export function createLatium(seed: string): Heightfield {
  return new Heightfield(seed);
}
